(() => {
  if (window.__WVG_INIT__) return;
  window.__WVG_INIT__ = true;

  const instances = new WeakMap();

  function toBool(value, fallback) {
    if (value === undefined || value === null || value === "") return fallback;
    return value === true || value === "true" || value === "1";
  }

  function parseUrls(raw) {
    if (!raw) return [];
    let parsed = raw;
    try { parsed = JSON.parse(raw); } catch (_) {}

    if (Array.isArray(parsed)) {
      return parsed.map((u) => String(u || "").trim()).filter(Boolean);
    }
    if (typeof parsed === "string") {
      return parsed.split("||").map((u) => u.trim()).filter(Boolean);
    }
    return [];
  }

  function escapeAttr(value) {
    return String(value || "")
      .replace(/&/g, "&amp;")
      .replace(/"/g, "&quot;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;");
  }

  function parsePlayZone(value, fallback) {
    if (value === undefined || value === null || value === "") return fallback;

    const raw = String(value).trim().toLowerCase();
    if (!raw) return fallback;
    if (raw === "false") return false;

    let ratio = NaN;

    if (raw.endsWith("%")) {
      ratio = Number(raw.slice(0, -1)) / 100;
    } else if (raw.includes("/")) {
      const parts = raw.split("/");
      if (parts.length === 2) {
        const numerator = Number(parts[0]);
        const denominator = Number(parts[1]);
        if (Number.isFinite(numerator) && Number.isFinite(denominator) && denominator !== 0) {
          ratio = numerator / denominator;
        }
      }
    } else {
      ratio = Number(raw);
    }

    if (!Number.isFinite(ratio) || ratio <= 0 || ratio > 1) return fallback;
    return ratio;
  }

  class WorkerVideoGrid {
    constructor(root) {
      this.root = root;
      this.urls = parseUrls(root.dataset.videoUrls);
      this.workerUrl = root.dataset.workerUrl;
      this.autoplay = toBool(root.dataset.autoplay, true);
      this.loop = toBool(root.dataset.loop, true);
      this.muted = toBool(root.dataset.muted, true);
      this.controls = toBool(root.dataset.controls, true);
      this.poster = parseUrls(root.dataset.videoPoster);
      this.playZone = parsePlayZone(root.dataset.playZone, 1 / 3);

      this.sourceAttached = false;
      this.blobByUrl = new Map();
      this.objectUrls = [];
      this.activeIndexes = new Set();

      this.videos = [];
      this.checkRaf = 0;
      this.handleViewportChange = this.scheduleVisibilityCheck.bind(this);
      this.handleVisibilityChange = this.onVisibilityChange.bind(this);
    }

    mount() {
      this.render();
      this.startWorker();
      this.observe();
    }

    render() {
      if (!this.urls.length) {
        this.root.innerHTML = `<div class="wvg-status">no video</div>`;
        return;
      }

      this.root.innerHTML = `
        <div class="wvg-grid">
          ${this.urls.map((_, i) => {
            const poster = this.poster[i] || this.poster[0] || "";
            return `
              <article class="wvg-card">
                <video class="wvg-video" poster="${escapeAttr(poster)}" data-index="${i}" ${this.controls ? "controls" : ""} ${this.muted ? "muted" : ""} ${this.loop ? "loop" : ""} playsinline preload="none"></video>
              </article>
            `;
          }).join("")}
        </div>
      `;
      this.statusEl = this.root.querySelector("[data-status]");
    }

    setStatus(text) {
      if (this.statusEl) this.statusEl.textContent = text;
    }

    startWorker() {
      if (!this.urls.length || !("Worker" in window)) {
        this.setStatus("worker unavailable, fallback to direct video src");
        return;
      }

      this.worker = new Worker(this.workerUrl);
      this.worker.addEventListener("message", (event) => {
        const data = event.data || {};

        if (data.type === "ready") {
          const blob = new Blob([data.buffer], { type: data.mime || "video/mp4" });
          const objectUrl = URL.createObjectURL(blob);
          this.blobByUrl.set(data.url, objectUrl);
          this.objectUrls.push(objectUrl);
          if (this.activeIndexes.size > 0) {
            this.attachSources();
            this.syncPlayback();
          }
        }

        if (data.type === "progress") {
          this.setStatus(`worker preload ${data.completed}/${data.total}`);
        }

        if (data.type === "done") {
          this.setStatus(`worker preload done, total ${data.total}`);
        }

        if (data.type === "error") {
          this.setStatus(`worker preload failed: ${data.url}`);
        }
      });

      this.worker.postMessage({
        type: "preload-many",
        urls: this.urls,
        concurrency: 3
      });
    }

    observe() {
      this.videos = Array.from(this.root.querySelectorAll(".wvg-video"));
      if (!this.videos.length) return;

      document.addEventListener("visibilitychange", this.handleVisibilityChange);

      if (this.playZone === false) {
        this.activateAllVideos();
        this.attachSources();
        this.syncPlayback();
        return;
      }

      window.addEventListener("scroll", this.handleViewportChange, { passive: true });
      window.addEventListener("resize", this.handleViewportChange);
      window.addEventListener("orientationchange", this.handleViewportChange);

      this.scheduleVisibilityCheck();
    }

    onVisibilityChange() {
      if (document.hidden) {
        this.pauseAll();
        return;
      }

      if (this.playZone === false) {
        this.activateAllVideos();
        this.attachSources();
        this.syncPlayback();
        return;
      }

      this.scheduleVisibilityCheck();
    }

    scheduleVisibilityCheck() {
      if (this.checkRaf) return;
      this.checkRaf = window.requestAnimationFrame(() => {
        this.checkRaf = 0;
        this.updateActiveVideos();
      });
    }

    isInPlayZone(video) {
      if (this.playZone === false) return true;

      const rect = video.getBoundingClientRect();
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;
      if (!viewportHeight) return false;

      const zoneHeight = viewportHeight * this.playZone;
      const zoneTop = (viewportHeight - zoneHeight) / 2;
      const zoneBottom = zoneTop + zoneHeight;

      return rect.bottom > zoneTop && rect.top < zoneBottom;
    }

    activateAllVideos() {
      this.activeIndexes.clear();
      this.videos.forEach((video) => {
        const idx = Number(video.dataset.index);
        if (!Number.isNaN(idx)) this.activeIndexes.add(idx);
      });
    }

    updateActiveVideos() {
      if (document.hidden) {
        this.pauseAll();
        return;
      }

      if (this.playZone === false) {
        this.activateAllVideos();
        this.attachSources();
        this.syncPlayback();
        return;
      }

      let hasActive = false;

      this.videos.forEach((video) => {
        const idx = Number(video.dataset.index);
        if (Number.isNaN(idx)) return;

        if (this.isInPlayZone(video)) {
          this.activeIndexes.add(idx);
          hasActive = true;
        } else {
          this.activeIndexes.delete(idx);
        }
      });

      if (hasActive) this.attachSources();
      this.syncPlayback();
    }

    attachSources() {
      if (this.sourceAttached) return;

      this.videos.forEach((video) => {
        const idx = Number(video.dataset.index);
        const rawUrl = this.urls[idx];
        const src = this.blobByUrl.get(rawUrl) || rawUrl;
        if (!video.src) {
          video.src = src;
          video.load();
        }
      });

      this.sourceAttached = true;
    }

    syncPlayback() {
      this.videos.forEach((video) => {
        const idx = Number(video.dataset.index);
        if (!this.activeIndexes.has(idx)) {
          video.pause();
          return;
        }

        if (!this.autoplay) return;
        const p = video.play();
        if (p && typeof p.catch === "function") p.catch(() => {});
      });
    }

    pauseAll() {
      this.videos.forEach((video) => video.pause());
      this.activeIndexes.clear();
    }

    destroy() {
      this.pauseAll();

      window.removeEventListener("scroll", this.handleViewportChange);
      window.removeEventListener("resize", this.handleViewportChange);
      window.removeEventListener("orientationchange", this.handleViewportChange);
      document.removeEventListener("visibilitychange", this.handleVisibilityChange);

      if (this.checkRaf) {
        window.cancelAnimationFrame(this.checkRaf);
        this.checkRaf = 0;
      }

      if (this.worker) this.worker.terminate();
      this.objectUrls.forEach((u) => URL.revokeObjectURL(u));
    }
  }

  function mount(scope = document) {
    scope.querySelectorAll("[data-worker-video-grid]").forEach((root) => {
      if (instances.has(root)) return;
      const app = new WorkerVideoGrid(root);
      app.mount();
      instances.set(root, app);
    });
  }

  function unmount(scope = document) {
    scope.querySelectorAll("[data-worker-video-grid]").forEach((root) => {
      const app = instances.get(root);
      if (!app) return;
      app.destroy();
      instances.delete(root);
    });
  }

  document.addEventListener("DOMContentLoaded", () => mount());
  document.addEventListener("shopify:section:load", (e) => mount(e.target));
  document.addEventListener("shopify:section:unload", (e) => unmount(e.target));
})();
